import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Save, Play, Pause, ArrowLeft, Plus, Trash2, Settings, Copy,
  Mail, Tag, Users, Clock, GitBranch, MousePointer, Calendar,
  CheckSquare, Globe, MessageCircle, Edit, FileText, Hand,
  Shuffle, MailOpen, Zap, RefreshCw, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for node types
const NODE_ICONS = {
  trigger_form_submit: FileText,
  trigger_tag_added: Tag,
  trigger_list_added: Users,
  trigger_email_opened: Mail,
  trigger_email_clicked: MousePointer,
  trigger_date_based: Calendar,
  trigger_manual: Hand,
  action_send_email: Mail,
  action_add_tag: Tag,
  action_remove_tag: Tag,
  action_add_to_list: Users,
  action_remove_from_list: Users,
  action_update_field: Edit,
  action_send_whatsapp: MessageCircle,
  action_create_task: CheckSquare,
  action_webhook: Globe,
  condition_field_check: GitBranch,
  condition_tag_check: Tag,
  condition_email_engagement: MailOpen,
  condition_random_split: Shuffle,
  delay_time: Clock,
  delay_until: Clock,
  delay_until_day: Calendar
};

// Custom node component
const CustomNode = ({ data, selected }) => {
  const Icon = NODE_ICONS[data.nodeType] || Zap;
  
  const getNodeColor = () => {
    if (data.nodeType?.startsWith('trigger_')) return 'border-green-500 bg-green-50 dark:bg-green-900/20';
    if (data.nodeType?.startsWith('action_')) return 'border-blue-500 bg-blue-50 dark:bg-blue-900/20';
    if (data.nodeType?.startsWith('condition_')) return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
    if (data.nodeType?.startsWith('delay_')) return 'border-purple-500 bg-purple-50 dark:bg-purple-900/20';
    return 'border-slate-500 bg-slate-50 dark:bg-slate-800';
  };

  return (
    <div className={`px-4 py-3 rounded-lg border-2 shadow-sm min-w-[180px] ${getNodeColor()} ${selected ? 'ring-2 ring-accent' : ''}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5" />
        <span className="font-medium text-sm">{data.label}</span>
      </div>
      {data.description && (
        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{data.description}</p>
      )}
    </div>
  );
};

const nodeTypes = {
  custom: CustomNode
};

const JourneyBuilder = () => {
  const navigate = useNavigate();
  const { journeyId } = useParams();
  const { token } = useAuth();
  
  const [journey, setJourney] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodeTypes, setNodeTypes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialogs
  const [showAddNode, setShowAddNode] = useState(false);
  const [showNodeConfig, setShowNodeConfig] = useState(false);
  const [showJourneySettings, setShowJourneySettings] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [addNodeCategory, setAddNodeCategory] = useState('triggers');
  
  // Journey settings
  const [journeyName, setJourneyName] = useState('');
  const [journeyDescription, setJourneyDescription] = useState('');

  // Fetch node types
  useEffect(() => {
    const fetchNodeTypes = async () => {
      try {
        const res = await fetch(`${API_URL}/api/marketing/journeys/node-types`);
        const data = await res.json();
        if (data.success) {
          setNodeTypes(data.node_types);
        }
      } catch (error) {
        console.error('Error fetching node types:', error);
      }
    };
    fetchNodeTypes();
  }, []);

  // Fetch journey if editing
  useEffect(() => {
    const fetchJourney = async () => {
      if (!journeyId || journeyId === 'new') {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/marketing/journeys/${journeyId}?token=${token}`);
        const data = await res.json();
        
        if (data.success) {
          setJourney(data.journey);
          setJourneyName(data.journey.name);
          setJourneyDescription(data.journey.description || '');
          
          // Convert stored nodes/edges to ReactFlow format
          const flowNodes = (data.journey.nodes || []).map(n => ({
            id: n.id,
            type: 'custom',
            position: n.position,
            data: {
              ...n.data,
              nodeType: n.type,
              label: getNodeLabel(n.type),
              description: n.data?.description || ''
            }
          }));
          
          const flowEdges = (data.journey.edges || []).map(e => ({
            ...e,
            type: 'smoothstep',
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed }
          }));
          
          setNodes(flowNodes);
          setEdges(flowEdges);
        }
      } catch (error) {
        console.error('Error fetching journey:', error);
        toast.error('Failed to load journey');
      } finally {
        setLoading(false);
      }
    };

    fetchJourney();
  }, [journeyId, token]);

  const getNodeLabel = (type) => {
    const labels = {
      trigger_form_submit: 'Form Submitted',
      trigger_tag_added: 'Tag Added',
      trigger_list_added: 'Added to List',
      trigger_email_opened: 'Email Opened',
      trigger_email_clicked: 'Link Clicked',
      trigger_date_based: 'Date/Time',
      trigger_manual: 'Manual Entry',
      action_send_email: 'Send Email',
      action_add_tag: 'Add Tag',
      action_remove_tag: 'Remove Tag',
      action_add_to_list: 'Add to List',
      action_remove_from_list: 'Remove from List',
      action_update_field: 'Update Field',
      action_send_whatsapp: 'Send WhatsApp',
      action_create_task: 'Create Task',
      action_webhook: 'Call Webhook',
      condition_field_check: 'Check Field',
      condition_tag_check: 'Has Tag',
      condition_email_engagement: 'Email Engagement',
      condition_random_split: 'A/B Split',
      delay_time: 'Wait',
      delay_until: 'Wait Until',
      delay_until_day: 'Wait Until Day'
    };
    return labels[type] || type;
  };

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({
      ...params,
      type: 'smoothstep',
      animated: true,
      markerEnd: { type: MarkerType.ArrowClosed }
    }, eds));
  }, [setEdges]);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setShowNodeConfig(true);
  }, []);

  const handleAddNode = (nodeType) => {
    const newId = `node-${Date.now()}`;
    const newNode = {
      id: newId,
      type: 'custom',
      position: { x: 250, y: nodes.length * 150 + 100 },
      data: {
        nodeType: nodeType.type,
        label: nodeType.label,
        description: nodeType.description,
        config: {}
      }
    };
    
    setNodes((nds) => [...nds, newNode]);
    setShowAddNode(false);
    toast.success(`Added ${nodeType.label} node`);
  };

  const handleDeleteNode = () => {
    if (!selectedNode) return;
    
    setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
    setShowNodeConfig(false);
    setSelectedNode(null);
    toast.success('Node deleted');
  };

  const handleSaveJourney = async () => {
    if (!journeyName.trim()) {
      toast.error('Please enter a journey name');
      return;
    }

    setSaving(true);
    
    try {
      // Convert ReactFlow format to storage format
      const storageNodes = nodes.map(n => ({
        id: n.id,
        type: n.data.nodeType,
        data: n.data.config || {},
        position: n.position
      }));
      
      const storageEdges = edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        label: e.label
      }));

      const payload = {
        name: journeyName,
        description: journeyDescription,
        trigger_type: 'manual', // Default, will be overridden if trigger node exists
        nodes: storageNodes,
        edges: storageEdges
      };

      // Find trigger type from nodes
      const triggerNode = storageNodes.find(n => n.type?.startsWith('trigger_'));
      if (triggerNode) {
        payload.trigger_type = triggerNode.type.replace('trigger_', '');
      }

      let res;
      if (journeyId && journeyId !== 'new') {
        res = await fetch(`${API_URL}/api/marketing/journeys/${journeyId}?token=${token}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${API_URL}/api/marketing/journeys?token=${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      const data = await res.json();

      if (data.success) {
        toast.success('Journey saved successfully');
        if (!journeyId || journeyId === 'new') {
          navigate(`/marketing/journeys/${data.journey.id}`);
        }
        setJourney(data.journey);
      } else {
        toast.error(data.detail || 'Failed to save journey');
      }
    } catch (error) {
      toast.error('Error saving journey');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!journeyId || journeyId === 'new') {
      toast.error('Save the journey first');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/marketing/journeys/${journeyId}/activate?token=${token}`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Journey activated');
        setJourney(prev => ({ ...prev, status: 'active' }));
      } else {
        toast.error(data.detail || 'Failed to activate journey');
      }
    } catch (error) {
      toast.error('Error activating journey');
    }
  };

  const handlePause = async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/journeys/${journeyId}/pause?token=${token}`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Journey paused');
        setJourney(prev => ({ ...prev, status: 'paused' }));
      } else {
        toast.error(data.detail || 'Failed to pause journey');
      }
    } catch (error) {
      toast.error('Error pausing journey');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col" data-testid="journey-builder">
      {/* Header */}
      <div className="h-16 border-b bg-white dark:bg-slate-900 px-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/journeys')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <div>
            <Input
              value={journeyName}
              onChange={(e) => setJourneyName(e.target.value)}
              placeholder="Journey Name"
              className="border-0 text-lg font-semibold focus-visible:ring-0 p-0 h-auto"
            />
          </div>
          {journey?.status && (
            <Badge variant={
              journey.status === 'active' ? 'success' :
              journey.status === 'paused' ? 'warning' : 'secondary'
            }>
              {journey.status}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowJourneySettings(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button variant="outline" size="sm" onClick={handleSaveJourney} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
          {journey?.status === 'active' ? (
            <Button variant="outline" size="sm" onClick={handlePause}>
              <Pause className="w-4 h-4 mr-2" />
              Pause
            </Button>
          ) : (
            <Button size="sm" onClick={handleActivate} disabled={!journeyId || journeyId === 'new'}>
              <Play className="w-4 h-4 mr-2" />
              Activate
            </Button>
          )}
        </div>
      </div>

      {/* Main Canvas */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodeTypes={{ custom: CustomNode }}
          fitView
          snapToGrid
          snapGrid={[15, 15]}
        >
          <Background gap={20} size={1} />
          <Controls />
          <MiniMap 
            nodeColor={(node) => {
              if (node.data?.nodeType?.startsWith('trigger_')) return '#22c55e';
              if (node.data?.nodeType?.startsWith('action_')) return '#3b82f6';
              if (node.data?.nodeType?.startsWith('condition_')) return '#eab308';
              if (node.data?.nodeType?.startsWith('delay_')) return '#a855f7';
              return '#64748b';
            }}
          />
          
          {/* Add Node Panel */}
          <Panel position="top-left">
            <Button onClick={() => setShowAddNode(true)} className="shadow-lg">
              <Plus className="w-4 h-4 mr-2" />
              Add Node
            </Button>
          </Panel>
        </ReactFlow>
      </div>

      {/* Add Node Dialog */}
      <Dialog open={showAddNode} onOpenChange={setShowAddNode}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Add Node</DialogTitle>
            <DialogDescription>Select a node type to add to your journey</DialogDescription>
          </DialogHeader>

          <div className="flex gap-4">
            {/* Category Tabs */}
            <div className="w-40 space-y-1">
              {['triggers', 'actions', 'conditions', 'delays'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setAddNodeCategory(cat)}
                  className={`w-full text-left px-3 py-2 rounded-lg capitalize text-sm ${
                    addNodeCategory === cat
                      ? 'bg-accent text-white'
                      : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Node List */}
            <ScrollArea className="flex-1 h-[400px]">
              <div className="space-y-2 pr-4">
                {nodeTypes?.[addNodeCategory]?.map((nodeType) => {
                  const Icon = NODE_ICONS[nodeType.type] || Zap;
                  return (
                    <button
                      key={nodeType.type}
                      onClick={() => handleAddNode(nodeType)}
                      className="w-full text-left p-3 rounded-lg border hover:border-accent hover:bg-accent/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                        <div>
                          <p className="font-medium text-sm">{nodeType.label}</p>
                          <p className="text-xs text-slate-500">{nodeType.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Node Configuration Dialog */}
      <Dialog open={showNodeConfig} onOpenChange={setShowNodeConfig}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure Node</DialogTitle>
          </DialogHeader>

          {selectedNode && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                {(() => {
                  const Icon = NODE_ICONS[selectedNode.data?.nodeType] || Zap;
                  return <Icon className="w-6 h-6" />;
                })()}
                <div>
                  <p className="font-medium">{selectedNode.data?.label}</p>
                  <p className="text-sm text-slate-500">{selectedNode.data?.nodeType}</p>
                </div>
              </div>

              {/* Node-specific configuration would go here */}
              <p className="text-sm text-slate-500">
                Node configuration options will appear here based on the node type.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="destructive" size="sm" onClick={handleDeleteNode}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Node
            </Button>
            <Button onClick={() => setShowNodeConfig(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Journey Settings Dialog */}
      <Dialog open={showJourneySettings} onOpenChange={setShowJourneySettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Journey Settings</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Journey Name</Label>
              <Input
                value={journeyName}
                onChange={(e) => setJourneyName(e.target.value)}
                placeholder="Enter journey name"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={journeyDescription}
                onChange={(e) => setJourneyDescription(e.target.value)}
                placeholder="Describe this journey..."
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJourneySettings(false)}>
              Cancel
            </Button>
            <Button onClick={() => setShowJourneySettings(false)}>
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JourneyBuilder;
